import DeviceConfig from '@devices/config';
import { Keys } from '@devices/keys';
import { store } from '../../../store/store';
import type Focus from '../../../types/enums/focus';
import { IKeyboard } from '../../../types/interfaces/keyboard';
import { useEffect } from 'react';

const PRESSED_KEYS = 20;

/** Focus value at the start of the current keydown event (set in capture phase). */
let focusAtKeyDownStart: number | number[] | (number | string)[] | null = null;

function wasFocusedAtKeyDownStart(
  focus: Focus | number | Focus[] | number[] | undefined
): boolean {
  if (focusAtKeyDownStart === null) return false;
  if (focus === undefined) return false;
  if (focusAtKeyDownStart === focus) return true;
  const focusNum = typeof focus === 'number' ? focus : undefined;
  const startNum =
    typeof focusAtKeyDownStart === 'number' ? focusAtKeyDownStart : undefined;
  if (
    Array.isArray(focusAtKeyDownStart) &&
    focusNum !== undefined &&
    (focusAtKeyDownStart as (number | string)[]).includes(focusNum)
  )
    return true;
  if (
    Array.isArray(focus) &&
    startNum !== undefined &&
    (focus as number[]).includes(startNum)
  )
    return true;
  if (
    Array.isArray(focus) &&
    Array.isArray(focusAtKeyDownStart) &&
    (focusAtKeyDownStart as (number | string)[]).some((v) =>
      (focus as number[]).includes(Number(v))
    )
  )
    return true;
  return false;
}

function useKeyboard({
  focus = undefined,
  canBeFocused = true,
  stopPropagation = false,
  keyDownHandler,
  keyUpHandler,
  keyPressHandler,
  pointerUpHandler,
}: IKeyboard) {
  useEffect(() => {
    let lastKey = '';
    let pressed = 0;
    let lastFocus = -1;
    let keyTimeout: ReturnType<typeof setTimeout> | undefined = undefined;
    const isFocused = () => {
      const { focus: state } = store.getState();
      if (state.focus === null) return false;
      // if (DeviceConfig.capabilities.magicRemote && magicRemote.enabled)
      //   return false; // TODO: Uncomment this if we want to avoid first key down to be ignored if magic remote is enabled
      return (
        state.focus === focus ||
        ((state.focus as number[])?.length &&
          (state.focus as number[]).includes(focus as number))
      );
    };
    const keyDownCapture = () => {
      const slice = store.getState().focus;
      focusAtKeyDownStart =
        slice?.focus !== undefined && slice?.focus !== null
          ? slice.focus
          : null;
    };
    const keyDown = (event: KeyboardEvent) => {
      if (keyDownHandler) {
        const shouldHandle =
          (isFocused() || !canBeFocused) &&
          (canBeFocused ? wasFocusedAtKeyDownStart(focus) : true);
        if (shouldHandle) {
          const parsedKey =
            Keys[event.key] || Keys[event.code] || Keys[event.keyCode];
          if (lastKey === parsedKey) {
            pressed += 1;
          } else {
            pressed = 0;
          }
          lastKey = parsedKey;
          keyDownHandler(parsedKey, pressed > PRESSED_KEYS, pressed);
          clearTimeout(keyTimeout);
          if (DeviceConfig.capabilities.repeatSelect) {
            lastFocus = Array.isArray(focus) ? focus[0] ?? -1 : focus ?? -1;
          }
          if (parsedKey === 'SELECT' && keyPressHandler) {
            if (!DeviceConfig.capabilities.repeatSelect) {
              keyTimeout = setTimeout(() => {
                keyPressHandler();
              }, 2000);
            } else if (pressed > PRESSED_KEYS) {
              keyPressHandler();
            }
          }
          if (stopPropagation) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        }
      }
    };
    const keyUp = (event: KeyboardEvent) => {
      clearTimeout(keyTimeout);
      if (keyUpHandler && (isFocused() || !canBeFocused)) {
        const pressedKey =
          (event.key !== 'Unidentified' ? event.key : null) ||
          event.code ||
          event.keyCode;
        const parsedkey = Keys[pressedKey];
        if (
          lastKey === parsedkey ||
          (parsedkey === 'SELECT' &&
            !DeviceConfig.capabilities.repeatSelect &&
            lastFocus === focus)
        ) {
          keyUpHandler(parsedkey, pressed > PRESSED_KEYS, pressed);
        }
        if (stopPropagation) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }
      pressed = 0;
    };
    const pointerUp = () => {
      const { focus: state } = store.getState();
      if (pointerUpHandler && (state.focus === focus || !canBeFocused)) {
        pointerUpHandler();
      }
      pressed = 0;
    };
    const wheel = (event: Event) => {
      if (isFocused() || !canBeFocused) {
        let parsedKey = '';
        const wheelEvent = event as WheelEvent;
        if ((wheelEvent?.deltaY || 0) > 0) {
          parsedKey = 'DOWN';
        } else {
          parsedKey = 'UP';
        }
        if (lastKey === parsedKey) {
          pressed += 1;
        } else {
          pressed = 0;
        }
        lastKey = parsedKey;
        keyDownHandler &&
          keyDownHandler(parsedKey, pressed > PRESSED_KEYS, pressed);
        if (stopPropagation) {
          event.stopImmediatePropagation();
        }
      }
    };
    document.addEventListener('keydown', keyDownCapture, true);
    document.addEventListener('keydown', keyDown);
    document.addEventListener('keyup', keyUp);
    document.addEventListener('pointerup', pointerUp);
    document.addEventListener('mousewheel', wheel);
    return () => {
      clearTimeout(keyTimeout);
      document.removeEventListener('keydown', keyDownCapture, true);
      document.removeEventListener('keydown', keyDown);
      document.removeEventListener('keyup', keyUp);
      document.removeEventListener('mousewheel', wheel);
      document.removeEventListener('pointerup', pointerUp);
    };
  }, []);
}

export default useKeyboard;
